import { RefObject, useEffect, useRef } from "react";

interface UseHeroAnimationProps {
  heroRef: RefObject<HTMLDivElement | null>;
  fullVideoRef: RefObject<HTMLDivElement | null>;
  maskedWrapperRef: RefObject<HTMLDivElement | null>;
  textRef: RefObject<HTMLDivElement | null>;
  scrollTriggerRef?: RefObject<HTMLDivElement | null>;
  aboutSectionRef?: RefObject<HTMLDivElement | null>;
}

export const useHeroAnimation = ({
  heroRef,
  fullVideoRef,
  maskedWrapperRef,
  textRef,
  scrollTriggerRef,
  aboutSectionRef,
}: UseHeroAnimationProps) => {
  const ctxRef = useRef<ReturnType<typeof import("gsap").gsap.context> | null>(
    null
  );
  const aboutTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const gsapModulesRef = useRef<{
    gsap: typeof import("gsap").gsap;
    ScrollTrigger: typeof import("gsap/ScrollTrigger").ScrollTrigger;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const triggerElement = scrollTriggerRef?.current ?? heroRef.current;
    if (!triggerElement) return;

    const setupAnimation = () => {
      if (!gsapModulesRef.current) return;

      const { gsap, ScrollTrigger } = gsapModulesRef.current;

      // Cleanup previous animation
      ctxRef.current?.revert();
      if (aboutTimelineRef.current) {
        aboutTimelineRef.current.scrollTrigger?.kill();
        aboutTimelineRef.current.kill();
        aboutTimelineRef.current = null;
      }

      ctxRef.current = gsap.context(() => {
        // Measure the actual logo element (child of wrapper) BEFORE applying any scale
        const containerHeight = heroRef.current?.offsetHeight ?? 0;
        const logoElement = maskedWrapperRef.current
          ?.firstElementChild as HTMLElement;
        const elementHeight = logoElement?.offsetHeight ?? 1;
        const scale = (containerHeight * 0.55) / elementHeight;

        gsap.set(maskedWrapperRef.current, {
          opacity: 0,
          scale: 4,
          force3D: true,
          rotation: 0.01,
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: triggerElement,
            start: "top top",
            end: "55% bottom",
            scrub: 1.5,
            pin: false,
          },
        });

        tl.to(
          fullVideoRef.current,
          { opacity: 0, duration: 0.08, ease: "none" },
          0
        );
        tl.to(
          maskedWrapperRef.current,
          { opacity: 1, duration: 0.08, ease: "none" },
          0
        );
        tl.to(
          maskedWrapperRef.current,
          {
            scale,
            duration: 0.85,
            ease: "power4.out",
            y: "-22%",
            force3D: true,
            rotation: 0.01,
          },
          0.08
        );
        tl.to(
          textRef.current,
          { opacity: 0, y: -50, duration: 0.3, ease: "power2.out" },
          0
        );
      }, heroRef);

      // Animate AboutCledar section - separate from context (sibling element)
      if (aboutSectionRef?.current) {
        gsap.set(aboutSectionRef.current, { y: 0 });

        const aboutTl = gsap.timeline({
          scrollTrigger: {
            trigger: triggerElement,
            start: "5% top",
            end: "15% top",
            scrub: 1.2,
          },
        });

        aboutTl.to(
          aboutSectionRef.current,
          {
            top: "-180px",
            duration: 0.15,
            ease: "power4.in",
          },
          0
        );

        aboutTimelineRef.current = aboutTl;
      }
    };

    const initAnimation = async () => {
      const { gsap } = await import("gsap");
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");

      gsap.registerPlugin(ScrollTrigger);
      gsapModulesRef.current = { gsap, ScrollTrigger };

      setupAnimation();
    };

    // Debounced resize handler to recalculate animation values
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setupAnimation();
      }, 150);
    };

    initAnimation();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      ctxRef.current?.revert();
      if (aboutTimelineRef.current) {
        aboutTimelineRef.current.scrollTrigger?.kill();
        aboutTimelineRef.current.kill();
      }
    };
  }, [
    scrollTriggerRef,
    heroRef,
    fullVideoRef,
    maskedWrapperRef,
    textRef,
    aboutSectionRef,
  ]);
};
